import { useRef } from 'react';
import { AutoFitCoverFields, useContainerWidth } from './CoverFullPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_PHOTO = 'https://images.unsplash.com/photo-1664101606938-e664f5852fac?w=600&h=800&fit=crop&crop=face';

// Match CoverSpread / inline preview.
const getFrameClipPath = (shape) =>
  shape === 'circle' ? 'circle(50% at 50% 50%)' :
  shape === 'oval'   ? 'ellipse(50% 50% at 50% 50%)' :
  'none';

/**
 * Single source of truth for rendering the FRONT face of a book cover.
 *
 * Used by every preview surface in the app:
 *   1. Catalog thumbnails (BookDetail.js cover gallery)
 *   2. Inline big preview (BookDetail.js "Diseño de portada")
 *   3. "Ampliar" zoom dialog (BookDetail.js)
 *   4. The front panel inside CoverSpread (Vista completa dialog)
 *   5. AdminPanel template cards
 *
 * Layout decisions are made HERE only, so a change in framing rules or
 * text auto-fit instantly propagates to every place a cover is shown.
 *
 * Render branches:
 *   - **framed**  → custom background image + protagonist photo placed
 *                   inside the photo_frame rectangle/circle/oval.
 *   - **design-only** → custom background image, no protagonist photo.
 *   - **photo**   → protagonist photo as the entire background + gradient.
 */
export const CoverFrontPanel = ({
  template,
  bookId,
  protagonistPhotoUrl,        // optional, falls back to Unsplash sample
  protagonistPhotoVersion = 0,
  fieldValues,                // {protagonist_name, title, subtitle, branding}
  photoAdjust,                // {zoom, offsetX, offsetY} for non-framed
}) => {
  const ref = useRef(null);
  const width = useContainerWidth(ref);

  const photoFrame = template?.photo_frame || null;
  // Three template archetypes:
  //   - 'framed': background image + protagonist photo inside a rect/circle
  //   - 'full' : protagonist photo as the full background + text overlay
  //              (the template "preview_image_path" is just a sample shot;
  //              the real book uses cover_photo_path).
  //   - <none>  : same as 'full' — protagonist photo as the entire bg.
  const frameType = photoFrame?.type;
  const isFramed = frameType === 'framed';
  const hasBgImg = template?.background_type === 'custom' && template?.preview_image_path;
  // Design-only = template with bg image and NO photo at all. That's a
  // very rare case (no 'framed' nor 'full' photo_frame); currently no
  // template uses it but kept for future-proofing.
  const isDesignOnly = hasBgImg && !frameType;

  const bgImgUrl = template?.template_id
    ? `${API_URL}/api/cover-templates/${template.template_id}/preview-image`
    : null;

  const protagonistSrc = protagonistPhotoUrl
    ? `${protagonistPhotoUrl}${protagonistPhotoUrl.includes('?') ? '&' : '?'}v=${protagonistPhotoVersion}`
    : FALLBACK_PHOTO;

  // Pick the back layer image.
  let backgroundImg;
  if (isDesignOnly || isFramed) backgroundImg = bgImgUrl;
  else backgroundImg = protagonistSrc;

  const showPhotoTopLayer = isFramed && bgImgUrl;
  // Gradient only when the protagonist photo is the FULL background and
  // there's text overlay — gives the text contrast.
  const showGradient = !isFramed && !isDesignOnly;
  const bgColor = (isFramed && photoFrame?.bg_color) ? photoFrame.bg_color : '#1a1a1a';
  const fc = template?.fields_config || {};

  // Photo zoom/pan only applies when the protagonist is the full background.
  // For framed mode we use object-position (handled in renderFramedPhoto)
  // which gives the user the full 0-100% encuadre range without any
  // black-margin exposure regardless of photo/frame aspect ratio.
  const photoTransform = (!isFramed && !isDesignOnly && photoAdjust) ? {
    transform: `scale(${photoAdjust.zoom || 1})`,
    transformOrigin: `${50 + (photoAdjust.offsetX || 0)}% ${50 + (photoAdjust.offsetY || 0)}%`,
    objectFit: 'cover',
    objectPosition: `${50 + (photoAdjust.offsetX || 0)}% ${50 + (photoAdjust.offsetY || 0)}%`,
  } : { objectFit: 'cover' };

  const renderFramedPhoto = () => {
    if (!isFramed) return null;
    const fr = photoFrame;
    const left = (fr.x || 50) - (fr.width || 40) / 2;
    const top = (fr.y || 40) - (fr.height || 50) / 2;
    const clipPath = getFrameClipPath(fr.shape);
    const borderRadius = fr.shape === 'rectangle' ? '4px' : '0';
    // The browser handles the encuadre natively via object-position:
    // a value of 0% lines the LEFT/TOP edge of the photo with the
    // LEFT/TOP edge of the frame; 100% lines RIGHT/BOTTOM. The frame
    // is ALWAYS fully covered (object-fit: cover) so no black border
    // can ever show, regardless of photo or frame aspect ratio.
    // Drag offsetX/Y arrive in -50..+50 range (BookDetail's drag
    // handler), which we map to 0..100 % object-position.
    const adjust = photoAdjust || { zoom: 1, offsetX: 0, offsetY: 0 };
    const posX = Math.max(0, Math.min(100, 50 + (adjust.offsetX || 0)));
    const posY = Math.max(0, Math.min(100, 50 + (adjust.offsetY || 0)));
    return (
      <div
        className="absolute z-10 overflow-hidden"
        style={{
          left: `${left}%`, top: `${top}%`,
          width: `${fr.width || 40}%`, height: `${fr.height || 50}%`,
          clipPath: clipPath !== 'none' ? clipPath : undefined,
          borderRadius,
          transform: fr.rotation ? `rotate(${fr.rotation}deg)` : undefined,
          background: '#000',
        }}
        data-testid="framed-protagonist-photo"
      >
        <img
          src={protagonistSrc}
          alt=""
          className="w-full h-full"
          onError={(e) => { if (e.currentTarget.src !== FALLBACK_PHOTO) e.currentTarget.src = FALLBACK_PHOTO; }}
          style={{
            objectFit: 'cover',
            objectPosition: `${posX}% ${posY}%`,
            transform: adjust.zoom > 1 ? `scale(${adjust.zoom})` : undefined,
            transformOrigin: `${posX}% ${posY}%`,
          }}
        />
      </div>
    );
  };

  return (
    <div
      ref={ref}
      className="absolute inset-0 overflow-hidden"
      style={{ background: bgColor }}
    >
      {backgroundImg && (
        <img
          src={backgroundImg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={photoTransform}
          onError={backgroundImg === protagonistSrc ? (e) => { if (e.currentTarget.src !== FALLBACK_PHOTO) e.currentTarget.src = FALLBACK_PHOTO; } : undefined}
        />
      )}
      {showGradient && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/15" />
      )}
      {showPhotoTopLayer && renderFramedPhoto()}
      {Object.keys(fc).length > 0 && (
        <AutoFitCoverFields
          fields={fc}
          fieldValues={fieldValues}
          containerWidth={width}
        />
      )}
    </div>
  );
};

export default CoverFrontPanel;
